import React, { Component } from 'react';
import _ from 'underscore';
import emitter from '../../util/eventEmitter.js';
import PropertyValue from '../../util/propertyValue.js';

class PropertyWarning extends Component {
  constructor(props) {
    super(props);

    this.requiredPropertyIDs = props.propertyOptions.map((opt) => opt.value);
    this.state = {
      propertyValuesList: this.convertToPropertyValuesList(
        this.props.propertyInfo,
      ),
    };
    // Bind must be done before addListener (alias `on`) so that removeListener can access the same registered function
    this.updateValueAndReferrers = this.updateValueAndReferrers.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.referenceID !== this.props.referenceID) {
      this.reviseValue(this.props.referenceID);
    }
  }

  componentDidMount() {
    emitter.on('propertyupdate', this.updateValueAndReferrers);
  }

  componentWillUnmount() {
    emitter.removeListener('propertyupdate', this.updateValueAndReferrers);
  }

  updateValueAndReferrers(property) {
    const shouldUpdateReferrers = true;

    if (property.hasRawValue()) {
      let newList = this.appendToList({}, property);

      this.performUpdate(newList, property, null, shouldUpdateReferrers);
    } else {
      this.updateToLatestValue(property, shouldUpdateReferrers);
    }
  }

  reviseValue(id) {
    let property = this.getOrCreatePropertyValue(id);

    if (property.isValueAbsent() || property.hasReference()) {
      this.updateToLatestValue(property);
    }
  }

  convertToPropertyValuesList(obj) {
    let newList = {};

    Object.keys(obj).forEach((id) =>
      this.appendToList(newList, this.newPropertyValue(obj[id])),
    );
    return newList;
  }

  newPropertyValue(propertyInfo) {
    let { id, value, reference, empty } = propertyInfo;

    return new PropertyValue(id, value, reference, empty);
  }

  getOrCreatePropertyValue(id) {
    return (
      this.state.propertyValuesList[id] || this.newPropertyValue({ id: id })
    );
  }

  performUpdate(newestList, property, baseID, shouldUpdateReferrers) {
    if (property.hasReference()) {
      this.setPropertyValueToBaseValue(newestList, property, baseID);
    }
    if (shouldUpdateReferrers) {
      this.updateAllLevelsOfReferrers(newestList, property);
    }

    this.updateStatePropertyValuesList(newestList);
  }

  updateToLatestValue(property, shouldUpdateReferrers = false) {
    let baseID = property.isValueAbsent()
        ? property.id
        : this.getBaseValueID(property),
      idsToRequest = this.getIDsToRequest(baseID);

    if (idsToRequest.length > 0) {
      this.requestValues(idsToRequest, property, baseID, shouldUpdateReferrers);
    } else {
      let newestList = Object.assign({}, this.state.propertyValuesList); // copy instead of reference obj

      this.performUpdate(newestList, property, baseID, shouldUpdateReferrers);
    }
  }

  appendToList(list, propertyValue) {
    return Object.assign(list, { [propertyValue.id]: propertyValue });
  }

  updateStatePropertyValuesList(newList) {
    let extendStateList = Object.assign(
      {},
      this.state.propertyValuesList,
      newList,
    );

    this.setState(() => ({ propertyValuesList: extendStateList }));
  }

  /**
   * Return the property ID that is last referenced and does not have value in list.
   *
   * Traverse property references until the value of next reference is absent or raw.
   * If final reference value is raw, then no value will be requested from server.
   * @param {PropertyValue} propertyValue
   *                        the property current property
   * @return {number|null} id|null
   */
  getBaseValueID(propertyValue) {
    let reference = propertyValue.hasReference()
      ? this.getOrCreatePropertyValue(propertyValue.reference)
      : false;

    if (reference === false || reference.hasRawValue()) {
      return null;
    }
    if (reference.isValueAbsent()) {
      return reference.id;
    }
    if (this.isCircularDependency(reference, propertyValue)) {
      return propertyValue.id;
    }
    return this.getBaseValueID(reference);
  }

  getIDsToRequest(requiredID) {
    let missingIDs = this.requiredPropertyIDs.filter(
      (id) =>
        !Object.prototype.hasOwnProperty.call(
          this.state.propertyValuesList,
          id,
        ),
    );

    if (requiredID) {
      missingIDs.push(requiredID);
    }
    return missingIDs;
  }

  requestValues(idsToRequest, property, baseID, shouldUpdateReferrers) {
    let urlQuery = idsToRequest
      .map((id) => `properties[]=${encodeURIComponent(id)}`)
      .join('&');

    fetch(
      `${window.location.href.replace(/#/, '')}/ip_append_references?${urlQuery}`,
    )
      .then((response) => response.json())
      .then((data) => {
        let newestList = this.convertToPropertyValuesList(data);

        this.performUpdate(newestList, property, baseID, shouldUpdateReferrers);
      });
  }

  setPropertyValueToBaseValue(newList, targetProperty, baseID) {
    targetProperty.empty = newList[baseID].empty;
    this.appendToList(newList, targetProperty);
  }

  getReferrers(referee) {
    return _.pick(this.state.propertyValuesList, (property) =>
      this.hasValidReferenceTo(property, referee),
    );
  }

  hasValidReferenceTo(property, referee) {
    if (!property.hasReference()) {
      return false;
    }
    return (
      property.reference == referee.id &&
      !this.isCircularDependency(referee, property)
    );
  }

  isCircularDependency(referee, referrer) {
    if (!referee.reference) {
      return false;
    }
    return referee.reference == referrer.id;
  }

  setReferrersToBaseValue(list, referrers, referee) {
    for (let key in referrers) {
      referrers[key].empty = referee.empty;
    }
    Object.assign(list, referrers);
  }

  isSelectedPropertyEmpty(state, id) {
    if (!Object.prototype.hasOwnProperty.call(state.propertyValuesList, id)) {
      return false;
    }
    return state.propertyValuesList[id].empty;
  }

  updateAllLevelsOfReferrers(
    list,
    referee,
    baseId = referee.id,
    nextReferees = null,
  ) {
    let referrers = referee ? this.getReferrers(referee) : {};

    if (_.isEmpty(referrers)) {
      return;
    }
    if (_.isEmpty(nextReferees)) {
      nextReferees = Object.keys(referrers);
    }
    this.setReferrersToBaseValue(list, referrers, list[baseId]);
    return this.updateAllLevelsOfReferrers(
      list,
      list[nextReferees.shift()],
      baseId,
      nextReferees,
    );
  }

  render() {
    return (
      <If
        condition={
          this.props.referenceID &&
          this.isSelectedPropertyEmpty(this.state, this.props.referenceID)
        }
      >
        <div className='ui warning message append-warning'>
          <b>Warning:</b> The variable reference{' '}
          <b>{this.props.referenceLabel}</b> value is empty.
        </div>
      </If>
    );
  }
}

module.exports = PropertyWarning;
